import { FormikHelpers } from 'formik';
import { useState } from 'react';
import axios from '~/utils/axios';

export const UNSUPPORTED_COUNTRY_ERROR_KEY = 'UNSUPPORTED_COUNTRY';

export interface AddressProps {
  regionCode: string;
  locality: string;
  administrativeArea: string;
  postalCode: string;
  addressLines: string[];
}
export type Address = {
  address: AddressProps;
};

export type ValidationResponse = {
  result: {
    verdict: {
      inputGranularity: string;
      validationGranularity: string;
      hasInferredComponents: boolean;
      hasUnconfirmedComponents: boolean;
      hasReplacedComponents: boolean;
    };
    address: {
      formattedAddress: string;
      addressComponents: {
        componentType: string;
        value: string;
        confirmationLevel: string;
      }[];
      unconfirmedComponentTypes: string[];
      postalAddress: AddressProps;
    };
    geocode: {
      location: {
        latitude: string;
        longitude: string;
      };
    };
  };
};

export type ValidateAddressResult = {
  resultAddressValidation: ValidationResponse | null;
  validation: ValidationAnalysis;
};
export const useAddressValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateAddress = async (address: Address): Promise<ValidateAddressResult> => {
    setIsValidating(true);
    setError(null);

    try {
      const response = await axios.post('/public/validate-address', address);
      const dataAnalyzed = analyzeAddressValidation(response.data, address.address);
      return {
        resultAddressValidation: response.data,
        validation: dataAnalyzed,
      };
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      // Handle unsupported country error, treat as valid but unsupported
      if (apiError === UNSUPPORTED_COUNTRY_ERROR_KEY) {
        return {
          resultAddressValidation: null,
          validation: {
            isValid: true,
            status: UNSUPPORTED_COUNTRY_ERROR_KEY,
            shouldShowErrorModal: false,
            shouldSuggestModal: false,
          },
        };
      }
      // If it's not an unsupported country error, set generic error message
      setError(apiError || 'Unexpected error');

      throw err;
    } finally {
      setIsValidating(false);
    }
  };

  return { validateAddress, isValidating, error };
};

export interface ErrorDetails {
  errorMessage: string;
  originalInput: string;
}

export interface ValidationAnalysis {
  isValid: boolean;
  errors?: string[];
  errorDetails?: ErrorDetails[];
  shouldSuggestModal: boolean;
  shouldShowErrorModal: boolean;
  status?: string;
}

export const analyzeAddressValidation = (
  data: ValidationResponse | null,
  originalInput: AddressProps,
): ValidationAnalysis => {
  const verdict = data?.result?.verdict;
  const address = data?.result?.address;
  const components = address?.addressComponents || [];
  const unconfirmedTypes = address?.unconfirmedComponentTypes || [];

  const suggested: AddressProps = {
    regionCode: address?.postalAddress?.regionCode || '',
    locality: address?.postalAddress?.locality || '',
    administrativeArea: address?.postalAddress?.administrativeArea || '',
    postalCode: address?.postalAddress?.postalCode || '',
    addressLines: address?.postalAddress?.addressLines || [],
  };

  const errors: string[] = [];
  const errorDetails: ErrorDetails[] = [];

  if (!verdict) {
    return {
      isValid: false,
      errors: ['Address validation verdict could not be retrieved.'],
      shouldSuggestModal: false,
      shouldShowErrorModal: true,
    };
  }

  const {
    validationGranularity,
    hasUnconfirmedComponents,
    hasInferredComponents,
    hasReplacedComponents,
  } = verdict;

  const acceptedGranularities = ['PREMISE', 'PREMISE_PROXIMITY', 'SUB_PREMISE'];
  const weakGranularities = ['BLOCK', 'ROUTE', 'LOCALITY', 'OTHER'];

  const isStrictlyValid =
    acceptedGranularities.includes(validationGranularity) &&
    !hasUnconfirmedComponents &&
    !hasInferredComponents &&
    !hasReplacedComponents;

  const streetNumber = components.find((c: any) => c.componentType === 'street_number');

  const onlyIssueIsStreetNumber =
    validationGranularity === 'PREMISE_PROXIMITY' &&
    hasUnconfirmedComponents &&
    unconfirmedTypes?.length === 1 &&
    unconfirmedTypes[0] === 'street_number' &&
    streetNumber?.confirmationLevel === 'UNCONFIRMED_BUT_PLAUSIBLE';

  const fieldsDiffer =
    originalInput.regionCode !== suggested.regionCode ||
    originalInput.locality !== suggested.locality ||
    originalInput.administrativeArea !== suggested.administrativeArea ||
    originalInput.postalCode !== suggested.postalCode ||
    originalInput.addressLines?.[0] !== suggested.addressLines?.[0];

  const suggestionIsTooDifferent =
    fieldsDiffer && !acceptedGranularities.includes(validationGranularity);

  const shouldShowErrorModal =
    weakGranularities.includes(validationGranularity) || suggestionIsTooDifferent;

  const shouldSuggestModal =
    !shouldShowErrorModal &&
    !isStrictlyValid &&
    (fieldsDiffer || hasUnconfirmedComponents || hasInferredComponents || hasReplacedComponents);

  if (isStrictlyValid) {
    return {
      isValid: true,
      errors: [],
      shouldSuggestModal: false,
      shouldShowErrorModal: false,
    };
  }

  if (onlyIssueIsStreetNumber) {
    return {
      isValid: true,
      errors: ['Street number could not be fully confirmed. Please double-check it.'],
      shouldSuggestModal,
      shouldShowErrorModal,
    };
  }

  if (hasUnconfirmedComponents && unconfirmedTypes.length > 0) {
    const typeMap: Record<string, { message: string; key: keyof AddressProps }> = {
      country: {
        message: 'Country could not be confirmed.',
        key: 'regionCode',
      },
      postal_code: {
        message: 'Postal code could not be confirmed.',
        key: 'postalCode',
      },
      street_number: {
        message: 'Street number could not be confirmed.',
        key: 'addressLines',
      },
      administrative_area_level_1: {
        message: 'Region/State could not be confirmed.',
        key: 'administrativeArea',
      },
      locality: {
        message: 'City could not be confirmed.',
        key: 'locality',
      },
      route: {
        message: 'Street name could not be confirmed.',
        key: 'addressLines',
      },
    };

    unconfirmedTypes.forEach((type) => {
      const mapping = typeMap[type];
      if (mapping) {
        const value = originalInput?.[mapping.key];
        const displayValue = Array.isArray(value)
          ? value.join(' ')
          : value || '(No value provided)';
        errors.push(`${mapping.message}\n${displayValue}`);
        errorDetails.push({ errorMessage: mapping.message, originalInput: displayValue });
      } else {
        errors.push(`Component "${type}" could not be confirmed.`);
      }
    });
  }

  if (hasInferredComponents) {
    errors.push('Google inferred missing components automatically.');
  }

  if (hasReplacedComponents) {
    errors.push('Some components were replaced with suggested values.');
  }

  return {
    isValid: false,
    errors,
    errorDetails,
    shouldSuggestModal,
    shouldShowErrorModal,
  };
};

export const mapPostalAddressToFormik = (
  postal: AddressProps,
  setFieldValue: FormikHelpers<any>['setFieldValue'],
) => {
  setFieldValue('country', postal.regionCode);
  setFieldValue('city', postal.locality);
  setFieldValue('region', postal.administrativeArea);
  setFieldValue('state', postal.administrativeArea);
  setFieldValue('zip', postal.postalCode);
  setFieldValue('street', postal.addressLines?.[0] || '');
  setFieldValue('street2', postal.addressLines?.[1] ?? '');
};

export type AddressVerificationStatus =
  | 'unchecked'
  | 'verified'
  | 'unverified'
  | 'overridden'
  | 'unsupported';

export const updateAddressVerification = async (
  userId: number,
  addressVerification: AddressVerificationStatus,
) => {
  const response = await axios.post(`/auth/verify-address/${userId}`, {
    address_verification: addressVerification,
  });
  return response.data;
};
